<template>
  <div>
    <AppProgressBar
      :steps="steps"
      :current-step="currentStep"
      :style="progressBarStyle"
    />

    <transition
      :name="transition"
      mode="out-in"
      @after-leave="scrollToTop()"
    >
      <component
        :key="currentStep"
        ref="currentStep"
        class="step-component"
        :is="currentStep"
        @next="nextStep($event)"
      />
    </transition>
  </div>
</template>

<script>
/**
 * <step-handler></step-handler>
 * @desc Handles the step order logic for the application
 */

import AppProgressBar from '@/components/common/notifications/AppProgressBar'
import datalayer from '@/data-layer'
import LandingPage from './steps/landing-page/LandingPage'
import scroll from '@/mixins/scroll'
import submit from '@/mixins/submit'
import validation from '@/mixins/validation'
import 'url-polyfill'

export default {
  // Name
  name: 'step-handler',

  // Components
  components: {
    AppProgressBar,
    LandingPage,
    VehicleSelect: () => import(/* webpackChunkName: "step-application-v1" */ './steps/VehicleSelect'),
    BudgetSelect: () => import(/* webpackChunkName: "step-application-v1" */ './steps/BudgetSelect'),
    CurrentlyDriving: () => import(/* webpackChunkName: "step-application-v1" */ './steps/CurrentlyDriving'),
    CreditRating: () => import(/* webpackChunkName: "step-application-v1" */ './steps/CreditRating'),
    ConfirmEligibility: () => import(/* webpackChunkName: "step-application-v1" */ './steps/ConfirmEligibility'),
    AddressSelect: () => import(/* webpackChunkName: "step-application-v1" */ './steps/AddressSelect'),
    AddressDuration: () => import(/* webpackChunkName: "step-application-v1" */ './steps/AddressDuration'),
    AddressRentOwn: () => import(/* webpackChunkName: "step-application-v1" */ './steps/AddressRentOwn'),
    EmploymentStatus: () => import(/* webpackChunkName: "step-application-v1" */ './steps/EmploymentStatus'),
    EmploymentStatusOther: () => import(/* webpackChunkName: "step-application-v1" */ './steps/EmploymentStatusOther'),
    EmploymentStudent: () => import(/* webpackChunkName: "step-application-v1" */ './steps/EmploymentStudent'),
    EmploymentIncomeType: () => import(/* webpackChunkName: "step-application-v1" */ './steps/EmploymentIncomeType'),
    EmploymentIncome: () => import(/* webpackChunkName: "step-application-v1" */ './steps/EmploymentIncome'),
    AdditionalIncome: () => import(/* webpackChunkName: "step-application-v1" */ './steps/AdditionalIncome'),
    AdditionalIncomeAmounts: () => import(/* webpackChunkName: "step-application-v1" */ './steps/AdditionalIncomeAmounts'),
    EmploymentDuration: () => import(/* webpackChunkName: "step-application-v1" */ './steps/EmploymentDuration'),
    EmploymentJobDetails: () => import(/* webpackChunkName: "step-application-v1" */ './steps/EmploymentJobDetails'),
    NameDetails: () => import(/* webpackChunkName: "step-application-v1" */ './steps/NameDetails'),
    ContactDetails: () => import(/* webpackChunkName: "step-application-v1" */ './steps/ContactDetails')
  },

  // Mixins
  mixins: [scroll, validation, submit],

  // Data
  data () {
    return {
      currentStep: 'landing-page',
      stepIndex: 0,
      lastStep: undefined,
      progressBlacklist: ['landing-page'],
      transition: 'step-next',
      appHeaderInverted: true,
      steps: {
        'landing-page': {
          next: () => 'vehicle-select',
          progressLabel: '3 minutes from finish'
        },
        'vehicle-select': {
          next: () => 'budget-select',
          progressLabel: '3 minutes from finish'
        },
        'budget-select': {
          next: () => 'currently-driving',
          progressLabel: '3 minutes from finish'
        },
        'currently-driving': {
          next: () => 'credit-rating',
          progressLabel: '3 minutes from finish'
        },
        'credit-rating': {
          next: () => 'employment-status',
          progressLabel: '2-3 minutes from finish'
        },
        'employment-status': {
          next: () => {
            if (this.source_of_income === 'Other') {
              return 'employment-status-other'
            } else if (this.source_of_income === 'Student') {
              return 'employment-student'
            } else if (this.isEmployed) {
              return 'employment-income-type'
            } else {
              datalayer.pushEvent('event', 'income_details_skip')
              return 'employment-income'
            }
          },
          progressLabel: '2-3 minutes from finish'
        },
        'employment-status-other': {
          next: () => {
            datalayer.pushEvent('event', 'income_details_skip')
            return 'employment-income'
          },
          progressLabel: '2-3 minutes from finish'
        },
        'employment-student': {
          next: () => {
            if (this.isEmployed) {
              return 'employment-income-type'
            } else {
              datalayer.pushEvent('event', 'income_details_skip')
              return 'employment-income'
            }
          },
          progressLabel: '2-3 minutes from finish'
        },
        'employment-income-type': {
          next: () => 'employment-income',
          progressLabel: '2-3 minutes from finish'
        },
        'employment-income': {
          next: () => 'employment-duration',
          progressLabel: '2-3 minutes from finish'
        },
        'employment-duration': {
          next: () => {
            if (this.isEmployed) {
              return 'employment-job-details'
            } else {
              datalayer.pushEvent('event', 'emp_job_details_skip')
              return this.askForAdditionalIncome ? 'additional-income' : 'address-select'
            }
          },
          progressLabel: '2-3 minutes from finish'
        },
        'employment-job-details': {
          next: () => this.askForAdditionalIncome ? 'additional-income' : 'address-select',
          progressLabel: '1-2 minutes from finish'
        },
        'additional-income': {
          next: () => this.hasAdditionalIncome ? 'additional-income-amounts' : 'address-select',
          progressLabel: '1-2 minutes from finish'
        },
        'additional-income-amounts': {
          next: () => 'address-select',
          progressLabel: '1-2 minutes from finish'
        },
        'address-select': {
          next: () => 'address-duration',
          progressLabel: '1-2 minutes from finish'
        },
        'address-duration': {
          next: () => 'address-rent-own',
          progressLabel: '1-2 minutes from finish'
        },
        'address-rent-own': {
          next: () => 'confirm-eligibility',
          progressLabel: '1-2 minutes from finish'
        },
        'confirm-eligibility': {
          next: () => 'name-details',
          progressLabel: 'Less than 1 minute from finish'
        },
        'name-details': {
          next: () => 'contact-details',
          progressLabel: 'Less than 1 minute from finish'
        },
        'contact-details': {
          progressLabel: 'Less than 1 minute from finish'
        }
      },
      url: new URL(window.location.href)
    }
  },

  // Computed
  computed: {
    isEmployed () {
      return this.$store.state.isEmployed
    },
    source_of_income () {
      return this.$store.state.application.source_of_income_a
    },
    askForAdditionalIncome () {
      const income = this.$store.state.application.monthly_income
      return (income >= 1500 && income <= 1799)
    },
    hasAdditionalIncome () {
      return (this.$store.state.application.has_additional_income === 'Yes')
    },
    progressBarStyle () {
      return this.progressBarVisible
        ? { visibility: 'hidden' }
        : { visibility: 'visible' }
    },
    progressBarVisible () {
      return this.progressBlacklist.includes(this.currentStep)
    },
    invertAppHeaderLogo () {
      return this.currentStep
    }
  },

  // Watch
  watch: {
    progressBarVisible (newValue) {
      this.$bus.$emit('AppHeader:invert', newValue)
      this.$bus.$emit('AppFooter:hideBorder', newValue)
    },
    invertAppHeaderLogo () {
      this.$bus.$emit('AppHeader:invertLogo', this.currentStep)
    }
  },

  // Created
  created () {
    this.setDefaultStep()
    if (history.state && history.state.currentStep === 'contact-details' && this.submittedApp) {
      this.transition = undefined
      this.currentStep = history.state.currentStep
      this.stepIndex = history.state.stepIndex
    } else {
      history.replaceState({
        currentStep: this.currentStep,
        stepIndex: this.stepIndex,
        appHeaderInverted: this.appHeaderInverted
      }, this.currentStep)
    }
  },

  // Mounted
  mounted () {
    try {
      window.sessionStorage.removeItem('thanks-timer')
    } catch (error) {}

    this.setPopstateListener()
  },

  destroyed () {
    window.removeEventListener('popstate', this.handlePopstate)
  },

  // Methods
  methods: {
    async nextStep (next) {
      try {
        await this.validateStep(this.$refs.currentStep)
        this.transition = 'step-next'
        const nextStep = next || this.steps[this.currentStep].next()
        this.stepIndex++
        window.history.pushState({
          currentStep: nextStep,
          stepIndex: this.stepIndex
        }, this.currentStep)

        this.currentStep = nextStep
      } catch (error) {
        console.warn(error)
      }
    },

    async handlePopstate (event) {
      const eventIndex = event.state.stepIndex
      const invalidStepOrder = this.validateStepOrder(eventIndex, this.stepIndex, this.$refs.currentStep)

      if (invalidStepOrder) return

      if (eventIndex > this.stepIndex) {
        try {
          await this.validateStep(this.$refs.currentStep)
          this.transition = 'step-next'
          this.stepIndex = eventIndex
          this.currentStep = event.state.currentStep
        } catch (error) {
          console.warn(error.message)
        }
      } else {
        this.transition = 'step-prev'
        this.stepIndex = eventIndex
        this.currentStep = event.state.currentStep
      }
    },

    // Listen for popstate events, Update currentStep accordingly
    setPopstateListener () {
      window.addEventListener('popstate', this.handlePopstate, false)
    },

    scrollToTop () {
      if (this.transition !== 'step-next') return
      this.$nextTick(() => {
        window.scrollTo(0, 0)
      })
    },

    /**
     * Set Default Step
     * @desc If there's a 'start' query param, set that as the landing step. If the first
     * step is skipped, set the appropriate values/dataLayer events based on query params
     */
    setDefaultStep () {
      if (!this.url.searchParams.has('start')) return
      const start = Math.min(this.url.searchParams.get('start'), 2) || 0
      this.currentStep = Object.keys(this.steps)[start]
      if (start >= 1) datalayer.pushEvent('event', 'new_or_used_vehicle_continue')
      if (start === 2) {
        datalayer.pushObject({
          event: 'vehicle_type_continue',
          user_selected_vehicle_from: 'homepage'
        })
      }
    },

    setZeroDown () {
      if (!this.url.searchParams.has('zero_down')) return
      const zeroDownValue = this.url.searchParams.get('zero_down')
      const acceptedValues = ['yes', 'no']
      const valueIsValid = acceptedValues.includes(zeroDownValue.toLowerCase())
      if (!valueIsValid) return
      this.$store.updateApplication('zero_down', zeroDownValue)
      datalayer.pushEvent('event', `zero_down_${zeroDownValue}`)
    }
  }
}
</script>

<style scoped lang="scss">
.step-component {
  @include breakpoint('desktop-up') {
    margin: auto;
  }
}
</style>
